import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { campaignAnalytics } from 'utils/appRoutes';
import { getUrlParams } from 'utils/urlParams';
import { DATE_FORMAT_FULL_WITH_TIME, displayDate } from 'utils/date';
import { Badge, BADGE_VARIANTS } from 'ui/badge';
import { InfoBanner } from 'ui/banner';
import { FlexSpacer } from 'ui/flexSpacer/FlexSpacer';
import { Link } from 'ui/links/Link';
import { Container } from 'ui/container/Container';
import { BUTTON_SIZES, SecondaryButton } from 'ui/button';
import { Divider } from 'ui/divider';
import { Send } from 'ui/icons/action/Send';
import { SecondaryPageHeader, HeaderTitle } from 'ui/pageHeader';
import { Body2 } from 'ui/theme/typography/components';
import { RightTooltip } from 'ui/tooltip/Tooltip';
import { Skeleton } from 'ui/skeleton/Skeleton';
import { fetchSentMessageRequest, resendMessageRequest } from '../actions';
import { selectSentMessage, selectIsFetchingSentMessage } from '../selectors';
import { ViewSentEmailRequestPayload } from '../types';
import { ResendMessageModal } from './ResendMessageModal';
import {
  formatCorrespondent,
  formatDynamicBannerTitle,
  formatHeaderSubsectionText,
} from '../utils';
import { MessagePreview } from './MessagePreview';
import { DetailField } from './DetailField';
import {
  StyledDL,
  DataFeedList,
  DataFeedName,
  DataFeedId,
  Layout,
  CampaignValue,
} from './SentMessagePage.styled';

export const TEXT = {
  HEADER: 'View sent email message',
  MAIN: {
    TITLE: 'Message content',
    DATA_FEED: 'Data feed',
  },
  ASIDE: {
    TITLE: 'Details',
    CAMPAIGN: 'Campaign',
    SEND_DATE: 'Send date',
    SENT_TO: 'Sent to',
    CC: 'Cc',
    BCC: 'Bcc',
    FROM: 'From',
    REPLY_TO: 'Reply to',
  },
  RESEND_BUTTON: 'Resend message',
  STORED_MESSAGE: {
    BADGE: 'Stored message copy',
    TOOLTIP: 'The message shown has been stored and should match what was originally sent.',
  },
  DYNAMIC_MESSAGE: {
    BANNER: `This is a recreation based on the campaign’s original template, and up-to-date user profile, data feed, and Catalog data (as needed).`,
  },
  NONE: 'None',
};

export const SentMessagePage = () => {
  const { search } = useLocation();
  const { messageId, recipient } = getUrlParams(search);
  const dispatch = useDispatch();
  const sentMessage = useSelector(selectSentMessage);
  const isFetchingSentMessage = useSelector(selectIsFetchingSentMessage);
  const {
    ccEmails,
    bccEmails,
    campaignId = '',
    campaignName = '',
    dataFeeds,
    displayableUserIdentifier = '',
    fromEmail,
    fromName,
    html,
    messageMedium,
    messageSentAt = '',
    plainText,
    preheaderText = TEXT.NONE,
    recipientEmail = '',
    replyTo = TEXT.NONE,
    subject = TEXT.NONE,
  } = sentMessage;
  const [isResendMessageModalOpen, setIsResendMessageModalOpen] = useState(false);

  useEffect(() => {
    const payload: ViewSentEmailRequestPayload = { messageId, recipient };
    dispatch(fetchSentMessageRequest(payload));
  }, [dispatch, messageId, recipient]);

  const resend = () => {
    const payload = {
      messageId,
      messageMedium,
      recipient,
    };
    dispatch(resendMessageRequest(payload, campaignName));
    setIsResendMessageModalOpen(false);
  };

  /**
    It is possible for the user's current email (recipient) to be different
    from their email at send time (recipientEmail). Links use the
    current email and message details show the send-time email.
  */
  const sentAt = displayDate(messageSentAt, DATE_FORMAT_FULL_WITH_TIME);

  const headerSubsectionText = formatHeaderSubsectionText(recipientEmail, sentAt);

  const ccText = ccEmails?.join(', ');
  const bccText = bccEmails?.join(', ');

  return (
    <>
      <SecondaryPageHeader
        breadcrumb={{
          content: `User Profile (${displayableUserIdentifier})`,
          href: `/users/profiles/${recipient}`,
        }}
        isAngularLink={true}
        pageActions={
          <SecondaryButton
            onClick={() => setIsResendMessageModalOpen(true)}
            size={BUTTON_SIZES.LARGE}
          >
            <Send />
            {TEXT.RESEND_BUTTON}
          </SecondaryButton>
        }
        pageSubSection={isFetchingSentMessage ? '' : headerSubsectionText}
      >
        {campaignName || TEXT.HEADER}
      </SecondaryPageHeader>
      <Layout>
        <Container variant="secondary">
          {isFetchingSentMessage ? (
            <Skeleton height={600} />
          ) : (
            <FlexSpacer flexDirection="column" alignItems="stretch" gap="cardGap">
              <FlexSpacer justifyContent="space-between" alignItems="baseline">
                <HeaderTitle>{TEXT.MAIN.TITLE}</HeaderTitle>
                {sentMessage?.isStored && (
                  <RightTooltip tooltipContent={TEXT.STORED_MESSAGE.TOOLTIP}>
                    <Badge variant={BADGE_VARIANTS.RECURRING}>{TEXT.STORED_MESSAGE.BADGE}</Badge>
                  </RightTooltip>
                )}
              </FlexSpacer>
              {sentMessage?.isStored === false && (
                // Must verify that isStored is false and not merely falsey
                <InfoBanner title={formatDynamicBannerTitle(recipientEmail)}>
                  {TEXT.DYNAMIC_MESSAGE.BANNER}
                </InfoBanner>
              )}
              <MessagePreview
                html={html}
                plainText={plainText}
                preheaderText={preheaderText}
                subject={subject}
              />
            </FlexSpacer>
          )}
        </Container>

        <div>
          <HeaderTitle>{TEXT.ASIDE.TITLE}</HeaderTitle>
          <Divider />
          <StyledDL>
            <DetailField isFetchingValue={isFetchingSentMessage} name={TEXT.ASIDE.CAMPAIGN}>
              {campaignId ? (
                <Link href={campaignAnalytics(campaignId)}>{campaignName}</Link>
              ) : (
                <CampaignValue>{campaignName || TEXT.NONE}</CampaignValue>
              )}
            </DetailField>
            {sentAt && (
              <DetailField isFetchingValue={isFetchingSentMessage} name={TEXT.ASIDE.SEND_DATE}>
                <span>{sentAt}</span>
              </DetailField>
            )}
            {recipientEmail && (
              <DetailField isFetchingValue={isFetchingSentMessage} name={TEXT.ASIDE.SENT_TO}>
                <span>{recipientEmail}</span>
              </DetailField>
            )}
            <DetailField
              isFetchingValue={isFetchingSentMessage}
              name={TEXT.ASIDE.CC}
              value={ccText || TEXT.NONE}
            />
            <DetailField
              isFetchingValue={isFetchingSentMessage}
              name={TEXT.ASIDE.BCC}
              value={bccText || TEXT.NONE}
            />
            {fromEmail && (
              <DetailField
                isFetchingValue={isFetchingSentMessage}
                name={TEXT.ASIDE.FROM}
                value={formatCorrespondent(fromName, fromEmail)}
              />
            )}
            <DetailField
              isFetchingValue={isFetchingSentMessage}
              name={TEXT.ASIDE.REPLY_TO}
              value={replyTo}
            />
            {dataFeeds && (
              <DetailField isFetchingValue={isFetchingSentMessage} name={TEXT.MAIN.DATA_FEED}>
                {dataFeeds && dataFeeds.length > 0 ? (
                  <DataFeedList>
                    {dataFeeds.map(({ name, id }) => {
                      return (
                        <li key={id}>
                          <Body2>
                            <DataFeedName>{name}</DataFeedName>
                            <DataFeedId>{` (ID ${id})`}</DataFeedId>
                          </Body2>
                        </li>
                      );
                    })}
                  </DataFeedList>
                ) : (
                  <DataFeedId>None</DataFeedId>
                )}
              </DetailField>
            )}
          </StyledDL>
        </div>
      </Layout>
      {isResendMessageModalOpen && (
        <ResendMessageModal
          bccText={bccText}
          ccText={ccText}
          closeModal={() => setIsResendMessageModalOpen(false)}
          onConfirm={resend}
          recipient={recipient}
        />
      )}
    </>
  );
};
